import { JobSubtask, JobTaskStatus } from '@root/globalTypes';
import { JobSubTaskInput } from '../input';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@components/ui/button';
import { useOutsideClick } from '@hooks/useOutsideClick';
import { Form, FormField } from '@components/ui/form';
import { useJobContext } from '@contexts/job';
import { parseDecimalValue } from '@utils/formHelpers';
import { useMaterialUpdateForm } from '@forms/materials/update';
import { useAccess } from '@hooks/access';

interface JobSubtaskConcreteUpdateProps {
  data: JobSubtask;
  status?: JobTaskStatus;
}

export const JobSubtaskConcreteUpdate = ({ data, status }: JobSubtaskConcreteUpdateProps) => {
  const { t } = useTranslation('app');
  const formRef = useRef<HTMLFormElement>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const { archived } = useJobContext();
  const access = useAccess();

  const material = data?.materials?.[0];
  const disabled = status === 'completed' || data.completed || archived || access.cad;

  useOutsideClick(formRef, () => setIsVisible(false));

  const {
    form,
    fieldArray: { fields, replace },
    onSubmit,
  } = useMaterialUpdateForm({
    material,
    setIsVisible,
    successHandler: () => {},
  });

  const { handleSubmit, reset, setValue } = form;

  useEffect(() => {
    if (!material) return;
    reset();
    const options = material.options || [];
    replace(
      options.map((el) => ({
        id: el.id || '',
        name: el.name || '',
        unit: el.unit || '',
        valueType: el.valueType || '',
        doubleEntryRequired: el.doubleEntryRequired || false,
        finalQuantity: el.finalQuantity != null ? String(el.finalQuantity) : '',
        finalQuantityRepeat: el.finalQuantity != null ? String(el.finalQuantity) : '',
        required: Boolean(el.required),
      })),
    );
  }, [reset, material, replace]);

  return (
    <Form {...form}>
      <form
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit(onSubmit)}
        onFocus={() => setIsVisible(true)}
        ref={formRef}
        className="grid grid-cols-[repeat(2,minmax(min-content,max-content)_minmax(0,1fr))] items-center gap-x-4 gap-y-1 px-4 py-2.5 max-[899px]:grid-cols-1 max-[899px]:px-0 max-[899px]:pb-0">
        {fields.map((option, index) => (
          <React.Fragment key={option.id}>
            {material?.options?.[index]?.includeEstimated && (
              <JobSubTaskInput
                disabled
                valueType={option?.valueType === 'number' ? 'number' : 'text'}
                value={
                  option.valueType === 'number'
                    ? parseDecimalValue(material?.options?.[index]?.estimatedQuantity || '')
                    : material?.options?.[index]?.estimatedQuantity || '-'
                }
                label={t('pages.job.info.inputs.estimated', { name: option.name })}
                placeholder={material?.options?.[index]?.estimatedQuantity || '0'}
                suffix={option?.unit || ''}
                rightAlignValue={option.valueType === 'number' && !!option.unit}
              />
            )}
            <FormField
              control={form.control}
              name={`optionsAttributes.${index}.finalQuantity`}
              render={({ field: { onChange, ...field } }) => (
                <JobSubTaskInput
                  {...field}
                  onChange={(v) => {
                    onChange(v.target.value);
                    setValue(`optionsAttributes.${index}.finalQuantityRepeat`, v.target.value);
                  }}
                  id={`final-quantity-${option?.id}`}
                  valueType={option?.valueType === 'number' ? 'number' : 'text'}
                  disabled={disabled}
                  placeholder={option?.finalQuantity || (option?.valueType === 'number' ? '0' : '')}
                  label={
                    material?.options?.[index]?.includeEstimated
                      ? t('pages.job.info.inputs.final', { name: option?.name })
                      : option?.name || ''
                  }
                  suffix={option?.unit || ''}
                  labelClassName={
                    material?.options?.[index]?.includeEstimated ? 'col-start-3' : 'col-start-1'
                  }
                  inputClassName={
                    material?.options?.[index]?.includeEstimated
                      ? 'col-start-4'
                      : 'col-start-2 col-end-[-1]'
                  }
                  rightAlignValue={option.valueType === 'number' && !!option.unit}
                />
              )}
            />
          </React.Fragment>
        ))}
        {isVisible && (
          <Button
            disabled={disabled}
            type="submit"
            className="col-start-4 w-full max-[899px]:col-start-1 max-[899px]:mt-2 max-[899px]:h-11">
            {t('actions.update')}
          </Button>
        )}
      </form>
    </Form>
  );
};
